import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Switch, Empty } from 'antd';
import {
  createTableChangeHandler,
  createCurrentPageKeysGetter,
  createScrollSync,
  createPaginationConfig,
} from '@client/utils/table/tableUtils';
import {
  createExpandedRowRender,
  createTableColumns,
} from '@client/feature/product/config/tableConfig';
import RiskSymbolsLegends from '@client/components/ui/RiskSymbolsLegends';
import ProductLegends from '@client/components/ui/productLegends';

/**
 * Clean and performant results table component
 */
const ResultItems = ({ data, onPageChange, onSortChange, currentSorting, hideSelectionTags, onHideSelectionTagsChange, selectedRowKeys, onSelectedRowKeysChange, showSelectedOnly, onShowSelectedOnlyChange }) => {
  const { t } = useTranslation();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [showRiskStyle, setShowRiskStyle] = useState(() => localStorage.getItem('sage-show-bordered-table') !== 'false');

  const handleShowRiskStyleChange = (checked) => {
    setShowRiskStyle(checked);
    localStorage.setItem('sage-show-bordered-table', String(checked));
  };
  const tableWrapperRef = useRef(null);
  const tableHeaderRef = useRef(null);


  /**
 * Legend section component
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} Legend section
 */
const LegendSection = ({ t }) => (
  <div className="flex justify-end">
    <div className="rounded-2xl bg-gray-50 border border-gray-200 px-6 py-4">
      <p className='font-medium text-[16px] mb-[16px]'>Légende : </p>
      <div className="space-y-4">
        <ProductLegends />
        <RiskSymbolsLegends title={t('product|Risk symbols :')} />
      </div>
    </div>
  </div>
);

  const results = data?.results || [];
  const pageSize = data?.size || 20;

  const filteredResults = useMemo(
    () => {
      const nonSeriesResults = results.filter((item) => !item.is_series);
      return showSelectedOnly
        ? nonSeriesResults.filter((item) => selectedRowKeys.includes(item.id))
        : nonSeriesResults;
    },
    [showSelectedOnly, results, selectedRowKeys]
  );

  const getCurrentPageKeys = useMemo(
    () => createCurrentPageKeysGetter(pageSize),
    [pageSize]
  );

  const columns = useMemo(
    () => createTableColumns(t, currentSorting),
    [t, currentSorting]
  );

  const handleTableChange = useMemo(
    () => createTableChangeHandler(onPageChange, onSortChange, columns),
    [onPageChange, onSortChange, columns]
  );

  const expandedCultureIds = data?.expanded_filters?.cultures || null;
  const expandedRowRender = useMemo(
    () => createExpandedRowRender(expandedCultureIds),
    [expandedCultureIds]
  );

  useEffect(() => {
    const tableHeader = tableHeaderRef.current;
    const tableBody = document.querySelector('.ant-table-content');
    return createScrollSync(tableHeader, tableBody);
  }, [data?.page, filteredResults]);

  useEffect(() => {
    if (hideSelectionTags) {
      setExpandedRowKeys([]);
    } else {
      const currentPageKeys = filteredResults
        .filter((item) => item.activity_sectors?.length || item.cultures?.length)
        .map((item) => item.id);
      setExpandedRowKeys(currentPageKeys);
    }
  }, [filteredResults, hideSelectionTags]);

  useEffect(() => {
    if (showSelectedOnly && selectedRowKeys.length === 0) {
      onShowSelectedOnlyChange(false);
    }
  }, [selectedRowKeys, showSelectedOnly, onShowSelectedOnlyChange]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: onSelectedRowKeysChange,
    }),
    [selectedRowKeys, onSelectedRowKeysChange]
  );

  const pagination = useMemo(
    () => createPaginationConfig(data, pageSize, t),
    [data, pageSize, t]
  );

  if (!results.length) {
    return (
      <div className="text-center py-8">
        <Empty
          description={t('product|No results found')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div>
      <div className='flex justify-between my-[32px] gap-[16px] flex-wrap'>
        <div className="flex flex-col gap-[16px] bg-[#FAFAFA] border border-[#F0F0F0] rounded-[16px] px-6 !py-4">
          <p className='font-medium text-[16px] mb-[16px]'>{t('product|Boutons d\'actions sur le tableau')} :</p>
          <div className='flex gap-[8px]'>
            <Switch
              checked={showSelectedOnly}
              onChange={onShowSelectedOnlyChange}
              disabled={selectedRowKeys.length === 0}
            />
            <span>{t('common|Show only my selections')}</span>
          </div>
          <div className='flex gap-[8px]'>
            <Switch
              checked={hideSelectionTags}
              onChange={onHideSelectionTagsChange}
            />
            <span>{t('common|Masquer les étiquettes de sélection')}</span>
          </div>
          <div className='flex gap-[8px]'>
            <Switch
              checked={showRiskStyle}
              onChange={handleShowRiskStyleChange}
            />
            <span>{t('common|Afficher le tableau avec bordures')}</span>
          </div>
        </div>
        <div>
          <LegendSection t={t} />
        </div>
      </div>

      <div className="responsive-table-wrapper" ref={tableWrapperRef}>
        <Table
          className={!showRiskStyle ? 'risk-results-table' : ''}
          columns={columns}
          dataSource={filteredResults}
          rowKey="id"
          bordered
          size="small"
          rowSelection={rowSelection}
          pagination={pagination}
          onChange={handleTableChange}
          loading={!data}
          hideOnSinglePage
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys((prev) =>
                expanded
                  ? [...prev, record.id]
                  : prev.filter((key) => key !== record.id)
              );
            },
            expandIcon: () => null,
            expandIconColumnIndex: -1,
          }}
        />
      </div>
    </div>
  );
};

export default ResultItems;
